import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { STUDIO_PALETTES } from '../components/organization-branding-view';
import { publicOrganizationReturn } from '../session/public-organization-routing';
import { normalizeReturnToPath } from '../session/return-path';
import { useSession } from '../session/session-provider';

export interface OrganizationPwaMetadata {
  readonly slug: string;
  readonly code?: string;
  readonly displayName: string;
  readonly themeColor: string;
  readonly iconVersion?: string;
}

const STORAGE_KEY = 'event.pwa.organization-slug';
const CHANGED_EVENT = 'event:public-organization-pwa-changed';
const SLUG = /^[a-z0-9][a-z0-9-]{0,62}$/;
const DEFAULT_ICON_VERSION = 'vcode-mark-v1';
const LOGO_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
let currentMetadata: OrganizationPwaMetadata | undefined;

function storedSlug(): string | undefined {
  try {
    const slug = window.sessionStorage.getItem(STORAGE_KEY);
    return slug && SLUG.test(slug) ? slug : undefined;
  } catch { return undefined; }
}

function meta(name: string, value: string): void {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!element) { element = document.createElement('meta'); element.name = name; document.head.append(element); }
  element.content = value;
}

function link(rel: string, href: string): void {
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!element) { element = document.createElement('link'); element.rel = rel; document.head.append(element); }
  element.href = href;
}

/** Only a public slug is persisted per window. Tokens, roles and membership never belong in PWA metadata. */
export function applyOrganizationPwaMetadata(metadata: OrganizationPwaMetadata | undefined): void {
  const valid = metadata && SLUG.test(metadata.slug) && metadata.displayName.trim()
    && /^#[a-fA-F0-9]{6}$/.test(metadata.themeColor) ? metadata : undefined;
  if (valid) {
    const version = valid.iconVersion && LOGO_ID.test(valid.iconVersion) ? valid.iconVersion.toLowerCase() : DEFAULT_ICON_VERSION;
    const icon = `/api/public/organizations/${valid.slug}/icons/192.png?v=${version}`;
    link('manifest', `/api/public/organizations/${valid.slug}/manifest.webmanifest`);
    link('apple-touch-icon', icon);
    link('icon', icon);
    meta('apple-mobile-web-app-title', valid.displayName);
    meta('theme-color', valid.themeColor);
    document.title = `${valid.displayName} — Event`;
  } else {
    link('manifest', '/manifest.webmanifest');
    link('apple-touch-icon', '/pwa/event-app-apple-180-v1.png');
    link('icon', '/pwa/event-app-192-v1.png');
    meta('apple-mobile-web-app-title', 'Event');
    meta('theme-color', '#0f7a67');
    document.title = 'Event';
  }
}

export function rememberOrganizationPwa(metadata: OrganizationPwaMetadata): void {
  if (!SLUG.test(metadata.slug) || !metadata.displayName.trim() || !/^#[a-fA-F0-9]{6}$/.test(metadata.themeColor)) return;
  currentMetadata = metadata;
  try { window.sessionStorage.setItem(STORAGE_KEY, metadata.slug); } catch { /* Memory-only metadata still works. */ }
  applyOrganizationPwaMetadata(metadata);
  window.dispatchEvent(new Event(CHANGED_EVENT));
}

function forgetOrganizationPwa(): void {
  currentMetadata = undefined;
  try { window.sessionStorage.removeItem(STORAGE_KEY); } catch { /* Never required for authorization. */ }
  applyOrganizationPwaMetadata(undefined);
}

/** Called only after the public organization profile has been resolved successfully. */
export function useOrganizationPwaMetadata(metadata: OrganizationPwaMetadata): void {
  useEffect(() => {
    rememberOrganizationPwa(metadata);
  }, [metadata.slug, metadata.code, metadata.displayName, metadata.themeColor, metadata.iconVersion]);
}

function parsePublicMetadata(value: unknown, slug: string): OrganizationPwaMetadata | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const data = value as { readonly slug?: unknown; readonly code?: unknown; readonly displayName?: unknown; readonly branding?: unknown };
  if (data.slug !== slug || typeof data.code !== 'string' || !data.code || typeof data.displayName !== 'string' || !data.displayName.trim()) return undefined;
  const branding = data.branding && typeof data.branding === 'object' ? data.branding as { readonly paletteId?: unknown; readonly displayName?: unknown; readonly logoAssetId?: unknown } : undefined;
  const palette = typeof branding?.paletteId === 'string' && Object.hasOwn(STUDIO_PALETTES, branding.paletteId)
    ? STUDIO_PALETTES[branding.paletteId as keyof typeof STUDIO_PALETTES] : STUDIO_PALETTES.violet;
  const displayName = typeof branding?.displayName === 'string' && branding.displayName.trim() ? branding.displayName : data.displayName;
  const iconVersion = typeof branding?.logoAssetId === 'string' && LOGO_ID.test(branding.logoAssetId) ? branding.logoAssetId.toLowerCase() : DEFAULT_ICON_VERSION;
  return { slug, code: data.code, displayName, themeColor: palette.accent, iconVersion };
}

/** Shared SW scope remains '/', while each organization manifest has its own identity/launch URL. */
export function OrganizationPwaMetadataSync(): null {
  const location = useLocation();
  const session = useSession();
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const changed = () => setRevision((value) => value + 1);
    const brandingChanged = () => { currentMetadata = undefined; changed(); };
    window.addEventListener(CHANGED_EVENT, changed);
    window.addEventListener('event:branding-changed', brandingChanged);
    return () => {
      window.removeEventListener(CHANGED_EVENT, changed);
      window.removeEventListener('event:branding-changed', brandingChanged);
    };
  }, []);

  useEffect(() => {
    const returnTo = normalizeReturnToPath(new URLSearchParams(location.search).get('returnTo'));
    const signInRoute = /^\/signin\/?$/.test(location.pathname);
    const explicit = publicOrganizationReturn(location.pathname)
      ?? (signInRoute ? publicOrganizationReturn(returnTo) : undefined);
    const retainedSignIn = signInRoute && Boolean(returnTo)
      && /^\/(attendee|admin|account|help)(\/|\?|$)/.test(returnTo ?? '');
    const retainingRoute = retainedSignIn || /^\/(auth\/callback|attendee|admin|account|help)(\/|$)/.test(location.pathname);
    const verifiedTenantSlug = retainingRoute && session.tenant?.publicSlug && SLUG.test(session.tenant.publicSlug)
      ? session.tenant.publicSlug : undefined;
    const slug = explicit?.slug ?? verifiedTenantSlug ?? (retainingRoute ? storedSlug() : undefined);
    if (!slug) { forgetOrganizationPwa(); return; }
    const compatible = (metadata: OrganizationPwaMetadata) => explicit || !session.tenant || metadata.code === session.tenant.code;
    if (currentMetadata?.slug === slug && currentMetadata.code) {
      if (compatible(currentMetadata)) applyOrganizationPwaMetadata(currentMetadata);
      else forgetOrganizationPwa();
      return;
    }
    // While validating a persisted slug, never show another organization's identity.
    applyOrganizationPwaMetadata(undefined);
    const controller = new AbortController();
    void fetch(`/api/public/organizations/${slug}`, { credentials: 'omit', cache: 'no-store', signal: controller.signal })
      .then(async (response) => response.ok ? parsePublicMetadata(await response.json() as unknown, slug) : undefined)
      .then((metadata) => {
        if (controller.signal.aborted) return;
        if (metadata && compatible(metadata)) rememberOrganizationPwa(metadata);
        else forgetOrganizationPwa();
      }).catch(() => {
        if (!controller.signal.aborted) forgetOrganizationPwa();
      });
    return () => controller.abort();
  }, [location.pathname, location.search, revision, session.tenant?.code, session.tenant?.publicSlug]);
  return null;
}
